import React, { Component, ReactNode } from "react";
import classnames from "classnames";

import { IconName } from "@formant/ui/component/common/Icon";

import { IMenuItem } from "../Menu/IMenuItem";
import { PillSelect } from "../PillSelect";
import * as styles from "./index.scss";
import { Only } from "./Only";

export interface IMultiSelectOption<T extends string | number | undefined> {
    value: T;
    label?: string;
    icon?: IconName;
    extraIcon?: ReactNode;
    className?: string;
}

export interface IMultiSelectProps<T extends string | number | undefined, U> {
    value: T[];
    options: IMultiSelectOption<T>[];
    title: string;
    disabled?: boolean;
    className?: string;
    onChange: (value: T[], context?: U) => void;
    context?: U;
    bottom?: boolean;
    menuStyle?: "blue" | "black";
}

export class MultiSelect<
    T extends string | number | undefined,
    U,
> extends Component<IMultiSelectProps<T, U>> {
    public render() {
        const {
            value,
            options,
            title,
            disabled,
            className,
            bottom,
            menuStyle,
        } = this.props;

        let label = title;
        if (value.length === 1) {
            const selected = options.find(_ => _.value === value[0]);
            label = `${title}: ${
                selected ? selected.label || `${selected?.value}` : ""
            }`;
        } else if (value.length > 1) {
            label = `${label}: ${value.length}`;
        }

        const empty = value.length === 0;

        return (
            <PillSelect
                title={title}
                label={label}
                items={options.map<IMenuItem<T>>(option => ({
                    value: option.value,
                    label: option.label,
                    icon: option.icon,
                    extraIcon: option.extraIcon,
                    selected: value.includes(option.value),
                    className: option.className,
                    hoverClass: styles.hover,
                    onSelected: this.onToggle,
                    extra: (
                        <Only
                            value={option.value}
                            className={styles.only}
                            onClicked={this.onOnly}
                        />
                    ),
                }))}
                empty={empty}
                disabled={disabled}
                closeOnSelect={false}
                className={classnames(styles["multi-select"], className)}
                onClear={this.onClear}
                bottom={bottom}
                menuStyle={menuStyle}
            />
        );
    }

    private onToggle = (option: T) => {
        const { value, onChange, context } = this.props;
        if (value.includes(option)) {
            onChange(
                value.filter(_ => _ !== option),
                context,
            );
        } else {
            onChange([...value, option], context);
        }
    };

    private onOnly = (option: T) => {
        const { onChange, context } = this.props;
        onChange([option], context);
    };

    private onClear = () => {
        const { onChange, context } = this.props;
        onChange([], context);
    };
}
